import { useEffect, useState } from "react";
import { BrandMark } from "../components/BrandMark";
import { api, ApiError } from "../lib/api";
import type { MeDTO, WorkspaceOptionDTO } from "@shared/api";

interface Props {
  onAuthenticated: (me: MeDTO) => void;
}

export function Login({ onAuthenticated }: Props) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [demoOpen, setDemoOpen] = useState(false);
  const [workspaces, setWorkspaces] = useState<WorkspaceOptionDTO[] | null>(
    null,
  );

  useEffect(() => {
    let cancelled = false;
    api.auth
      .workspaces()
      .then((data) => {
        if (!cancelled) setWorkspaces(data);
      })
      .catch(() => {
        /* non-fatal: demo hints just won't appear */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || !password) return;
    setSubmitting(true);
    setError(null);
    try {
      const me = await api.auth.login(email.trim(), password);
      onAuthenticated(me);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : String(err),
      );
      setSubmitting(false);
    }
  }

  function prefill(w: WorkspaceOptionDTO) {
    setEmail(w.defaultUserEmail);
    setPassword("demo");
  }

  return (
    <div className="bbc-login">
      <div className="bbc-login-shell">
        <div className="bbc-login-brand">
          <BrandMark size={48} />
          <div className="bbc-brand-sub">Business Central · Partner portal</div>
        </div>

        <section className="bbc-login-card">
          <div className="bbc-login-eyebrow">Partner portal</div>
          <h1 className="bbc-login-h1">Sign in to your workspace</h1>
          <p className="bbc-login-sub">
            Enter the email tied to your partner workspace. Passwords are
            stubbed in this demo — any value will sign you in as that user.
          </p>

          {error && <div className="bbc-error" role="alert">{error}</div>}

          <form className="bbc-login-form" onSubmit={submit} noValidate>
            <label className="bbc-field">
              <span className="bbc-field-label">Work email</span>
              <input
                type="email"
                name="email"
                autoComplete="username"
                placeholder="you@your-agency.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </label>

            <label className="bbc-field">
              <span className="bbc-field-label">Password</span>
              <input
                type="password"
                name="password"
                autoComplete="current-password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </label>

            <button
              type="submit"
              className="bbc-btn bbc-btn-primary bbc-login-submit"
              disabled={submitting || !email || !password}
            >
              {submitting ? "Signing in…" : "Sign in"}
            </button>
          </form>

          {workspaces && workspaces.length > 0 && (
            <details
              className="bbc-login-demo"
              open={demoOpen}
              onToggle={(e) =>
                setDemoOpen((e.currentTarget as HTMLDetailsElement).open)
              }
            >
              <summary>See demo accounts</summary>
              <ul className="bbc-login-demo-list">
                {workspaces.map((w) => (
                  <li key={w.id}>
                    <button
                      type="button"
                      className="bbc-login-demo-row"
                      onClick={() => prefill(w)}
                    >
                      <span className="bbc-login-demo-name">{w.name}</span>
                      <span className="bbc-login-demo-email mono">
                        {w.defaultUserEmail}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              <p className="bbc-login-demo-hint">
                Click a workspace to prefill the form. Any password works in
                the demo.
              </p>
            </details>
          )}

          <div className="bbc-login-foot">
            Sessions are signed and stored server-side. SSO and real password
            verification are stubbed for now — production deployments would
            slot real auth in here.
          </div>
        </section>

        <div className="bbc-login-note mono">
          Barose Business Central · v0.1 · Cloudflare Workers + D1
        </div>
      </div>
    </div>
  );
}
